import { usePharmacyStore } from '../stores/pharmacyStore';

/**
 * Pharmacy contact info printed on patient-facing artifacts (vial labels,
 * thermal receipts).
 *
 * Resolution order (getPharmacyContactInfo):
 *   1. Curated GSP entries below — the five GreenShield pharmacies keep the
 *      values audited under GP-622. Their MongoDB pharmacy documents visibly
 *      DISAGREE with these (checked 2026-07-23: the Ottawa doc says
 *      "700-2197 Riverside Drive K1H 7X3" while labels correctly ship
 *      "#400 - 1355 Bank St K1H 8K7"; London differs on address AND phone).
 *      Do NOT switch GSP to record-source without an ops data audit first.
 *   2. The pharmacy record from the pharmacy store (served by
 *      GET /api/pharmacies from the MongoDB pharmacy document) — every other
 *      pharmacy (e.g. Fillware sites like PharmAssist) is configured there,
 *      no code change needed to onboard one.
 *   3. Typed throw. NO silent fallback — the Mississauga-default fallback
 *      shipped wrong pickup addresses to Burnaby patients for months (GP-622).
 */
export interface PharmacyContactInfo {
  name: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  email: string;
  fax: string;
}

// PRX-79: the ONE sentence the SMS preview shows when a pharmacyId can't be
// resolved (getPharmacyContactInfo throws). Swap the WHOLE affected
// block/line for this — never fill it into individual fields — so it always
// reads as an obvious placeholder, never a real (or another pharmacy's real)
// address. The email preview's equivalent case renders the backend's own
// error sentence instead (see fetchEmailPreview below) — this constant is
// for the SMS path only.
export const PHARMACY_DETAILS_UNAVAILABLE_MESSAGE = 'Pharmacy details unavailable for this pharmacy.';

// Curated label info for the five GSP pharmacies (see resolution note above).
export const GSP_LABEL_INFO: Record<string, PharmacyContactInfo> = {
  // Mississauga
  '645b9c128158f06a385d5043': {
    name: 'GreenShield Pharmacy',
    phone: '(905) 232-2322',
    address: '#4 - 6975 Creditview Rd',
    city: 'Mississauga',
    province: 'ON',
    postalCode: 'L5N 8E9',
    email: 'pharmacy.mississauga@greenshield.ca',
    fax: '(905) 232-2553',
  },
  // Ottawa
  '64662e7c95bfef1e99ffbec9': {
    name: 'GreenShield Pharmacy',
    phone: '(613) 730-2322',
    address: '#400 - 1355 Bank St',
    city: 'Ottawa',
    province: 'ON',
    postalCode: 'K1H 8K7',
    email: 'pharmacy.ottawa@greenshield.ca',
    fax: '(613) 730-2553',
  },
  // London
  '64662eb695bfef1e99ffbed6': {
    name: 'GreenShield Pharmacy',
    phone: '(226) 330-0200',
    address: '629 Consortium Ct',
    city: 'London',
    province: 'ON',
    postalCode: 'N6E 2S8',
    email: 'pharmacy.london@greenshield.ca',
    fax: '(855) 640-1874',
  },
  // Manitoba (Winnipeg)
  '641b4aac2d2521c964108378': {
    name: 'GreenShield Pharmacy',
    phone: '(204) 202-8298',
    address: 'Unit G-675 Berry St',
    city: 'Winnipeg',
    province: 'MB',
    postalCode: 'R3H 1A7',
    email: 'pharmacy.central@greenshield.ca',
    fax: '(204) 201-3913',
  },
  // Burnaby (GP-622)
  '64876b9ea40f72e6937196a9': {
    name: 'GreenShield Pharmacy',
    phone: '(778) 244-1788',
    address: '#118 - 5589 Byrne Road',
    city: 'Burnaby',
    province: 'BC',
    postalCode: 'V5J 3J1',
    email: 'pharmacy.west@greenshield.ca',
    fax: '(604) 648-9939',
  },
};

export class MissingPharmacyInfoError extends Error {
  constructor(pharmacyId: string | undefined, detail: string) {
    super(
      `No printable pharmacy contact info for pharmacyId="${pharmacyId ?? '<undefined>'}" (${detail}). ` +
        `Refusing to print with a default pharmacy address. Fix the pharmacy document ` +
        `(name/phone/address incl. city+postal) in the pharmacies collection — labels read it via the pharmacy record.`,
    );
    this.name = 'MissingPharmacyInfoError';
  }
}

/** "6479515828" → "(647) 951-5828"; anything non-10-digit passes through. */
const formatPhone = (raw: string | undefined): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

export function getPharmacyContactInfo(pharmacyId?: string): PharmacyContactInfo {
  if (!pharmacyId) {
    throw new MissingPharmacyInfoError(pharmacyId, 'no pharmacyId provided');
  }

  const curated = GSP_LABEL_INFO[pharmacyId];
  if (curated) {
    return curated;
  }

  const { pharmacies, activePharmacy } = usePharmacyStore.getState();
  const record =
    pharmacies.find((p) => p.id === pharmacyId) ??
    (activePharmacy?.id === pharmacyId ? activePharmacy : undefined);

  if (!record) {
    throw new MissingPharmacyInfoError(pharmacyId, 'pharmacy not in store');
  }
  if (!record.name || !record.phone || !record.address || !record.city) {
    const missing = (['name', 'phone', 'address', 'city'] as const)
      .filter((k) => !record[k])
      .join(', ');
    throw new MissingPharmacyInfoError(pharmacyId, `pharmacy record missing: ${missing}`);
  }

  return {
    name: record.name,
    phone: formatPhone(record.phone),
    address: record.address,
    city: record.city,
    province: record.province ?? '',
    postalCode: record.postalCode ?? '',
    email: record.email ?? '',
    fax: formatPhone(record.fax),
  };
}
